import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Building2,
  Calendar,
  User,
  Award,
  BookOpen,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  FileText,
  Users,
  GraduationCap,
  Key,
  Briefcase,
  Target,
  Globe,
  Compass,
  Book,
  Activity
} from 'lucide-react';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { sanitizeInput } from '../../utils/sanitize';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/users/${id}`);

      if (data.success) {
        const sanitizedUser = sanitizeInput(data.data.fullName);
        setUser({
          ...data.data,
          fullName: sanitizedUser,
          email: sanitizeInput(data.data.email),
          mobile: sanitizeInput(data.data.mobile),
          userId: sanitizeInput(data.data.userId),
          studentId: data.data.studentId ? sanitizeInput(data.data.studentId) : null,
          itsupportId: data.data.itsupportId ? sanitizeInput(data.data.itsupportId) : null,
          recruiterId: data.data.recruiterId ? sanitizeInput(data.data.recruiterId) : null,
          companyId: data.data.companyId ? sanitizeInput(data.data.companyId) : null,
          branches: data.data.branches || [],
          rollNumber: data.data.rollNumber ? sanitizeInput(data.data.rollNumber) : null,
          role: sanitizeInput(data.data.role),
          status: sanitizeInput(data.data.status),
          address: data.data.address ? sanitizeInput(data.data.address) : null,
          qualification: data.data.qualification ? sanitizeInput(data.data.qualification) : null,
          specialization: data.data.specialization ? sanitizeInput(data.data.specialization) : null,
          department: data.data.department ? sanitizeInput(data.data.department) : null,
          experience: data.data.experience,
          bio: data.data.bio ? sanitizeInput(data.data.bio) : null,
          userPassword: data.data.userPassword ? sanitizeInput(data.data.userPassword) : null,
          createdAt: data.data.createdAt,
          profileImage: data.data.profileImage || null,
          registrationDetails: data.data.registrationDetails || null,
          website: data.data.website || null,
          aboutCompany: data.data.aboutCompany || null,
          hrName: data.data.hrName || null,
          hrEmail: data.data.hrEmail || null,
          hrMobile: data.data.hrMobile || null,
          gstCin: data.data.gstCin || null
        });

        // Fetch enrollments if student
        if (data.data.role === 'student') {
          try {
            const enrollRes = await api.get(`/enrollments?student=${id}`);
            if (enrollRes.data.success) {
              const sanitizedEnrollments = enrollRes.data.data.map(enrollment => ({
                ...enrollment,
                course: enrollment.course ? {
                  ...enrollment.course,
                  title: sanitizeInput(enrollment.course.title)
                } : null,
                status: sanitizeInput(enrollment.status),
                enrollmentDate: enrollment.enrollmentDate
              }));
              setEnrollments(sanitizedEnrollments);
            }
          } catch (error) {
            console.error('Failed to fetch enrollments:', error);
            if (error.response?.status === 404) {
              toast.error('Enrollments not found');
            } else if (error.response?.status === 403) {
              toast.error('You do not have permission to view enrollments');
            } else if (error.response?.status === 500) {
              toast.error('Server error while loading enrollments. Please try again later');
            } else if (!navigator.onLine) {
              toast.error('No internet connection. Please check your network');
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      if (error.response?.status === 404) {
        toast.error('User not found');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view this user');
      } else if (error.response?.status === 500) {
        toast.error('Server error while loading user details. Please try again later');
      } else if (!navigator.onLine) {
        toast.error('No internet connection. Please check your network');
      } else {
        toast.error('Failed to fetch user details');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircle }
    };

    const badge = badges[status] || badges.inactive;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold border ${badge.bg} ${badge.text} ${status === 'active' ? 'border-green-200' : 'border-gray-200'}`}>
        <Icon className="w-3.5 h-3.5 mr-1.5" />
        {sanitizeInput(status) ? sanitizeInput(status).charAt(0).toUpperCase() + sanitizeInput(status).slice(1) : 'N/A'}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const labels = {
      admin: 'Admin',
      college_admin: 'Institute Admin',
      student: 'Student',
      support: 'IT Support',
      recruiter: 'Recruiter'
    };

    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-[#1a3884]/5 text-[#1a3884] border border-[#1a3884]/15 dark:bg-[#4f7dff]/10 dark:text-[#4f7dff] dark:border-[#4f7dff]/25">
        {labels[role] || 'Student'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <AlertCircle className="w-16 h-16 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">User Not Found</h2>
        <p className="text-gray-500 mb-6">The user you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/users')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const getOverallCGPA = () => {
    if (!user || user.role !== 'student') return null;
    if (user.cgpa && !isNaN(parseFloat(user.cgpa))) return parseFloat(user.cgpa);
    if (user.academic?.overallCgpa && !isNaN(parseFloat(user.academic.overallCgpa))) return parseFloat(user.academic.overallCgpa);
    if (user.academicRecords && user.academicRecords.length > 0) {
      const recordsWithCgpa = user.academicRecords.filter(r => r.cgpa != null && !isNaN(parseFloat(r.cgpa)));
      if (recordsWithCgpa.length > 0) {
        const latestRecord = recordsWithCgpa.reduce((prev, current) => (prev.semester > current.semester) ? prev : current);
        return parseFloat(latestRecord.cgpa);
      }
    }
    if (user.registrationDetails?.cgpa && !isNaN(parseFloat(user.registrationDetails.cgpa))) return parseFloat(user.registrationDetails.cgpa);
    return null;
  };
  const overallCgpa = getOverallCGPA();
  const maxSem = user?.academicRecords && user.academicRecords.length > 0 
    ? Math.max(...user.academicRecords.map(r => r.semester)) 
    : (user?.academic?.uptoSemester || null);
  const activeBacklogsCount = user?.activeBacklogs !== undefined 
    ? user.activeBacklogs 
    : (user?.academic?.activeBacklogs !== undefined 
        ? user.academic.activeBacklogs 
        : (Array.isArray(user?.activeArrears) ? user.activeArrears.length : 0));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (user?.role === 'support') {
                navigate('/it-support');
              } else if (user?.role === 'recruiter') {
                navigate('/admin/recruiters');
              } else {
                navigate('/users');
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#00152E] dark:text-white">User Details</h1>
            <p className="text-sm text-gray-500 mt-1">Complete information about the user</p>
          </div>
        </div>
      </div>

      {/* User Profile Card — compact header row: navy avatar tile, name + ID,
          badges on the right. The old tall cover-banner with an overlapping
          avatar wasted half the viewport for no extra information. */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div>
          <div className="mb-0">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-xl flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                    {user.profileImage ? (
                      <img src={getFileUrl(user.profileImage)} className="w-full h-full object-cover" alt="Profile" />
                    ) : (
                      <span className="text-white font-bold text-2xl">
                        {sanitizeInput(user.fullName)?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-900 tracking-tight truncate">{sanitizeInput(user.fullName) || 'N/A'}</h2>
                    <p className="text-[12px] font-semibold text-gray-500 mt-0.5">{sanitizeInput(user.studentId || user.userId || user.itsupportId || user.recruiterId || user.rollNumber) || 'N/A'}</p>
                    {/* Show Company ID + Recruiter ID badges for recruiters */}
                    {user.role === 'recruiter' && (user.companyId || user.recruiterId) && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {user.companyId && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#00152E] text-white border border-[#00152E] tracking-wide">
                            <Building2 className="w-3 h-3" />
                            {user.companyId}
                          </span>
                        )}
                        {user.recruiterId && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#1a3884] text-white border border-[#1a3884] tracking-wide">
                            <User className="w-3 h-3" />
                            {user.recruiterId}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 shrink-0">
                  {overallCgpa !== null && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-[#1a3884]/5 text-[#1a3884] border border-[#1a3884]/15">
                      <GraduationCap className="w-3.5 h-3.5 mr-1.5" />
                      CGPA: {overallCgpa.toFixed(2)}
                      {maxSem && (
                        <span className="text-[10px] font-semibold ml-1.5 opacity-80">
                          (Upto Sem {maxSem})
                        </span>
                      )}
                    </span>
                  )}
                  {user.role === 'student' && (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold border ${
                      activeBacklogsCount > 0
                        ? 'bg-rose-50 text-rose-700 border-rose-200'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    }`}>
                      <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
                      Backlogs: {activeBacklogsCount === 0 ? '0 (Cleared)' : activeBacklogsCount}
                    </span>
                  )}
                  {getRoleBadge(user.role)}
                  {getStatusBadge(user.status)}
                </div>
              </div>
            </div>

            {/* Company Identity — shown only for recruiters */}
            {user.role === 'recruiter' && (user.companyId || user.qualification || (user.branches && user.branches.length > 0)) && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#1a3884]" />
                  Company Identity
                </h3>
                <div className="space-y-3">
                  {/* IDs row */}
                  <div className="flex flex-wrap gap-2">
                    {user.companyId && (
                      <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Company ID</span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#00152E] text-white border border-[#00152E] tracking-wide">
                          <Building2 className="w-3.5 h-3.5" />
                          {user.companyId}
                        </span>
                      </div>
                    )}
                    {user.recruiterId && (
                      <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Recruiter ID</span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#1a3884] text-white border border-[#1a3884] tracking-wide">
                          <User className="w-3.5 h-3.5" />
                          {user.recruiterId}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Company name / GST */}
                  {user.qualification && (
                    <div>
                      <p className="text-xs text-gray-500">Company Name</p>
                      <p className="text-sm font-semibold text-gray-900">{sanitizeInput(user.qualification)}</p>
                    </div>
                  )}
                  {user.gstCin && (
                    <div>
                      <p className="text-xs text-gray-500">GST / CIN</p>
                      <p className="text-sm font-medium text-gray-900 font-mono">{sanitizeInput(user.gstCin)}</p>
                    </div>
                  )}
                  {user.website && (
                    <div>
                      <p className="text-xs text-gray-500">Website</p>
                      <a href={user.website} target="_blank" rel="noreferrer" className="text-sm font-medium text-indigo-600 hover:underline">{sanitizeInput(user.website)}</a>
                    </div>
                  )}

                  {/* Branches */}
                  {user.branches && user.branches.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 font-semibold mb-2">Branches ({user.branches.length})</p>
                      <div className="space-y-2">
                        {user.branches.map((branch, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <div className="flex-shrink-0 mt-0.5">
                              <div className="w-7 h-7 bg-indigo-100 rounded-md flex items-center justify-center">
                                <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <p className="text-sm font-semibold text-gray-900 truncate">{sanitizeInput(branch.branchName) || `Branch ${idx + 1}`}</p>
                                {branch.branchId && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-[#00152E] text-white border border-[#00152E] tracking-wide shrink-0">
                                    {branch.branchId}
                                  </span>
                                )}
                              </div>
                              {(branch.city || branch.state) && (
                                <p className="text-xs text-gray-500">
                                  <MapPin className="w-3 h-3 inline mr-0.5" />
                                  {[branch.city, branch.district, branch.state, branch.pincode].filter(Boolean).join(', ')}
                                </p>
                              )}
                              <div className="flex flex-wrap gap-x-3 mt-1">
                                {branch.contactEmail && <p className="text-xs text-gray-500">{sanitizeInput(branch.contactEmail)}</p>}
                                {branch.contactMobile && <p className="text-xs text-gray-500">{sanitizeInput(branch.contactMobile)}</p>}
                              </div>
                            </div>
                            <span className={`shrink-0 self-start text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                              branch.status === 'active'
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-red-50 text-red-600 border-red-200'
                            }`}>
                              {branch.status || 'active'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {user.role === 'student' && (
        <div className="flex flex-wrap border-b border-gray-200 gap-2 mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: User },
            { id: 'academic', label: 'Academic Records', icon: GraduationCap },
            { id: 'experience', label: 'Work & Projects', icon: Briefcase },
            { id: 'preferences', label: 'Preferences & Goals', icon: Target },
            { id: 'extracurricular', label: 'Activities & Certificates', icon: Award },
            ...(enrollments.length > 0 ? [{ id: 'enrollments', label: 'Course Enrollments', icon: BookOpen }] : [])
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 border-b-2 font-semibold text-sm transition-all ${isActive
                  ? 'border-[#1a3884] text-[#1a3884]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Tabs Content */}
      {user.role === 'student' ? (
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100">Contact Information</h3>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[#00152E]/10 rounded-lg">
                    <Mail className="w-5 h-5 text-[#1a3884]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.email) || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[#00152E]/10 rounded-lg">
                    <Phone className="w-5 h-5 text-[#1a3884]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Mobile</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.mobile) || 'N/A'}</p>
                  </div>
                </div>
                {user.registrationDetails?.alternateMobile && (
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-[#00152E]/10 rounded-lg">
                      <Phone className="w-5 h-5 text-[#1a3884]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Alternate Mobile</p>
                      <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.alternateMobile)}</p>
                    </div>
                  </div>
                )}

                {(user.address || user.registrationDetails?.address) && (
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-[#00152E]/10 rounded-lg">
                      <MapPin className="w-5 h-5 text-[#1a3884]" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Address</p>
                      <p className="text-sm font-medium text-gray-900">
                        {user.registrationDetails?.address
                          ? `${user.registrationDetails.address.doorNo ? user.registrationDetails.address.doorNo + ', ' : ''}${user.registrationDetails.address.street ? user.registrationDetails.address.street + ', ' : ''}${user.registrationDetails.address.city || ''}, ${user.registrationDetails.address.state || ''}, ${user.registrationDetails.address.country || ''} - ${user.registrationDetails.address.pincode || ''}`
                          : sanitizeInput(user.address)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Personal Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100">Personal Information</h3>
                {user.registrationDetails?.nickname && (
                  <div>
                    <p className="text-xs text-gray-500">Nickname</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.nickname)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500">Date of Birth</p>
                  <p className="text-sm font-medium text-gray-900">
                    {user.registrationDetails?.dob
                      ? new Date(user.registrationDetails.dob).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                      : user.dateOfBirth
                        ? new Date(user.dateOfBirth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                        : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Gender</p>
                  <p className="text-sm font-medium text-gray-900">
                    {sanitizeInput(user.registrationDetails?.gender || user.gender) || 'N/A'}
                  </p>
                </div>
                {user.college && (
                  <div>
                    <p className="text-xs text-gray-500">Institute</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.college.collegeName)}</p>
                  </div>
                )}
                {user.degree && (
                  <div>
                    <p className="text-xs text-gray-500">Degree / Program</p>
                    <p className="text-sm font-medium text-gray-900">
                      {sanitizeInput(user.degree.fullName)} ({sanitizeInput(user.degree.abbreviation)})
                    </p>
                    {user.degree.specialization && (
                      <p className="text-xs text-gray-500 italic mt-0.5 pl-1">
                        Spec: {sanitizeInput(user.degree.specialization)}
                      </p>
                    )}
                  </div>
                )}
                {user.registrationDetails?.educationLevel && (
                  <div>
                    <p className="text-xs text-gray-500">Education Level</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.educationLevel)}</p>
                  </div>
                )}
                {(user.bio || user.registrationDetails?.bio) && (
                  <div>
                    <p className="text-xs text-gray-500">Bio</p>
                    <p className="text-sm text-gray-600 mt-1">{sanitizeInput(user.registrationDetails?.bio || user.bio)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'academic' && (
            <div className="space-y-6">
              {/* 10th and 12th details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 10th standard */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-[#1a3884]" />
                    10th Standard Details
                  </h3>
                  {user.registrationDetails?.tenthDetails ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">School Name</p>
                        <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.tenthDetails.schoolName) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Board</p>
                        <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.tenthDetails.board) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Year of Passing</p>
                        <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.tenthDetails.yearOfPassing) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{user.registrationDetails.tenthDetails.scoreType === 'CGPA' ? 'CGPA' : 'Percentage'}</p>
                        <p className="text-sm font-medium text-gray-900">{user.registrationDetails.tenthDetails.percentage ? `${sanitizeInput(user.registrationDetails.tenthDetails.percentage)}${user.registrationDetails.tenthDetails.scoreType === 'CGPA' ? ' CGPA' : ' %'}` : 'N/A'}</p>
                      </div>
                      {user.registrationDetails.tenthDetails.marksheet && (
                        <div>
                          <p className="text-xs text-gray-500">Marksheet</p>
                          <a href={getFileUrl(user.registrationDetails.tenthDetails.marksheet)} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs font-bold text-[#1a3884] hover:underline gap-1 mt-1">
                            <FileText className="w-3.5 h-3.5" /> View Document
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No 10th details provided</p>
                  )}
                </div>

                {/* 12th standard */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-[#1a3884]" />
                    12th Standard Details
                  </h3>
                  {user.registrationDetails?.twelfthDetails ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">School Name</p>
                        <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.twelfthDetails.schoolName) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Stream</p>
                        <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.twelfthDetails.stream) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Board</p>
                        <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.twelfthDetails.board) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Year of Passing</p>
                        <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.registrationDetails.twelfthDetails.yearOfPassing) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{user.registrationDetails.twelfthDetails.scoreType === 'CGPA' ? 'CGPA' : 'Percentage'}</p>
                        <p className="text-sm font-medium text-gray-900">{user.registrationDetails.twelfthDetails.percentage ? `${sanitizeInput(user.registrationDetails.twelfthDetails.percentage)}${user.registrationDetails.twelfthDetails.scoreType === 'CGPA' ? ' CGPA' : ' %'}` : 'N/A'}</p>
                      </div>
                      {user.registrationDetails.twelfthDetails.marksheet && (
                        <div>
                          <p className="text-xs text-gray-500">Marksheet</p>
                          <a href={getFileUrl(user.registrationDetails.twelfthDetails.marksheet)} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs font-bold text-[#1a3884] hover:underline gap-1 mt-1">
                            <FileText className="w-3.5 h-3.5" /> View Document
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No 12th details provided</p>
                  )}
                </div>
              </div>

              {/* Higher Education */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-[#1a3884]" />
                  Higher Education Records
                </h3>
                {user.registrationDetails?.higherEducation && user.registrationDetails.higherEducation.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {user.registrationDetails.higherEducation.map((edu, idx) => (
                      <div key={idx} className="border border-gray-100 rounded-lg p-4 bg-gray-50 space-y-2">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="text-[10px] uppercase font-bold text-[#1a3884] bg-[#1a3884]/10 px-2 py-0.5 rounded-full">{sanitizeInput(edu.qualificationLevel)}</span>
                            <h4 className="font-bold text-gray-900 text-sm mt-1">{sanitizeInput(edu.degreeFullName)} ({sanitizeInput(edu.degree)})</h4>
                          </div>
                          <span className="text-xs font-semibold text-gray-500">{sanitizeInput(edu.yearOfPassing)}</span>
                        </div>
                        <div className="text-xs space-y-1 text-gray-600">
                          <p><span className="font-semibold text-gray-700">Specialization:</span> {sanitizeInput(edu.specialization)}</p>
                          <p><span className="font-semibold text-gray-700">Institution:</span> {sanitizeInput(edu.institutionName)}</p>
                          <p><span className="font-semibold text-gray-700">University:</span> {sanitizeInput(edu.university)}</p>
                          <p><span className="font-semibold text-gray-700">Location:</span> {sanitizeInput(edu.location) || 'N/A'}</p>
                          <p><span className="font-semibold text-gray-700">CGPA / Percentage:</span> {sanitizeInput(edu.cgpaPercentage) || 'N/A'}</p>
                          <p><span className="font-semibold text-gray-700">Status:</span> {sanitizeInput(edu.degreeStatus) || 'N/A'}</p>
                        </div>
                        {edu.certificate && (
                          <div className="pt-2 border-t border-gray-200 mt-2">
                            <a href={getFileUrl(edu.certificate)} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs font-bold text-[#1a3884] hover:underline gap-1">
                              <FileText className="w-3.5 h-3.5" /> View Certificate
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No higher education records provided</p>
                )}
              </div>

              {/* Validated Semester Records */}
              {user.academicRecords && user.academicRecords.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-[#1a3884]" />
                    Validated Semester Performance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {[...user.academicRecords]
                      .sort((a, b) => a.semester - b.semester)
                      .map((rec, idx) => (
                      <div key={idx} className="border border-[#1a3884]/10 rounded-lg p-4 bg-[#f8fbff] space-y-3">
                        <div className="flex justify-between items-center">
                          <h4 className="font-bold text-[#0f225c] text-sm">Semester {rec.semester}</h4>
                          <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${
                            rec.status === 'Pass' || rec.status === 'Arrear Cleared' 
                            ? 'bg-emerald-100 text-emerald-800' 
                            : 'bg-rose-100 text-rose-800'
                          }`}>
                            {rec.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-white p-2 rounded border border-gray-100 text-center">
                            <p className="text-gray-500 font-semibold mb-0.5">SGPA</p>
                            <p className="text-base font-bold text-[#1a3884]">{rec.sgpa ? rec.sgpa.toFixed(2) : '0.00'}</p>
                          </div>
                          <div className="bg-white p-2 rounded border border-gray-100 text-center">
                            <p className="text-gray-500 font-semibold mb-0.5">Cum. CGPA</p>
                            <p className="text-base font-bold text-[#1a3884]">{rec.cgpa ? rec.cgpa.toFixed(2) : '0.00'}</p>
                          </div>
                        </div>
                        <div className="text-[11px] text-gray-500 font-semibold flex justify-between px-1 mt-1">
                          <span>Credits: {rec.earnedCredits}/{rec.totalCredits}</span>
                          <span>{rec.subjects?.length || 0} Subjects</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'experience' && (
            <div className="space-y-6">
              {/* Work Experience */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#1a3884]" />
                  Work Experience
                </h3>
                {user.registrationDetails?.workExperience && user.registrationDetails.workExperience.length > 0 ? (
                  <div className="space-y-4">
                    {user.registrationDetails.workExperience.map((exp, idx) => (
                      <div key={idx} className="border border-gray-100 rounded-lg p-4 bg-gray-50 space-y-2">
                        <div className="flex justify-between items-start flex-wrap gap-2">
                          <div>
                            <h4 className="font-bold text-gray-900 text-base">{sanitizeInput(exp.jobTitle)}</h4>
                            <p className="text-sm font-semibold text-[#1a3884]">{sanitizeInput(exp.companyName || exp.organizationName)}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-gray-500 font-semibold">{exp.startDate ? new Date(exp.startDate).toLocaleDateString() : ''} - {exp.currentlyWorking ? 'Present' : exp.endDate ? new Date(exp.endDate).toLocaleDateString() : ''}</span>
                            {exp.duration && <p className="text-xs text-gray-400 mt-0.5">{sanitizeInput(exp.duration)}</p>}
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <p><span className="font-semibold text-gray-700">Type:</span> {sanitizeInput(exp.experienceType)}</p>
                          {exp.industry && <p><span className="font-semibold text-gray-700">Industry:</span> {sanitizeInput(exp.industry)}</p>}
                          {exp.location && <p><span className="font-semibold text-gray-700">Location:</span> {sanitizeInput(exp.location)}</p>}
                          {exp.description && <p className="mt-2 text-gray-700 bg-white p-2.5 rounded border border-gray-100 leading-relaxed">{sanitizeInput(exp.description)}</p>}
                        </div>
                        {exp.certificate && (
                          <div className="pt-2 border-t border-gray-200 mt-2">
                            <a href={getFileUrl(exp.certificate)} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs font-bold text-[#1a3884] hover:underline gap-1">
                              <FileText className="w-3.5 h-3.5" /> View Experience Certificate
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No work experience records provided</p>
                )}
              </div>

              {/* Projects */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                  <Compass className="w-5 h-5 text-[#1a3884]" />
                  Academic / Personal Projects
                </h3>
                {user.registrationDetails?.projects && user.registrationDetails.projects.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {user.registrationDetails.projects.map((proj, idx) => (
                      <div key={idx} className="border border-gray-100 rounded-lg p-4 bg-gray-50 space-y-2">
                        <div className="flex justify-between items-start flex-wrap gap-2">
                          <h4 className="font-bold text-gray-900 text-sm">{sanitizeInput(proj.title)}</h4>
                          <span className="text-xs text-gray-500 font-medium">
                            {proj.startDate ? new Date(proj.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : ''} - {proj.endDate ? new Date(proj.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Present'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          {proj.domain && <p><span className="font-semibold text-gray-700">Domain:</span> {sanitizeInput(proj.domain)}</p>}
                          {proj.qualificationLevel && <p><span className="font-semibold text-gray-700">Level:</span> {sanitizeInput(proj.qualificationLevel)}</p>}
                          {proj.institution && <p><span className="font-semibold text-gray-700">Institution:</span> {sanitizeInput(proj.institution)}</p>}
                          {proj.teamType && <p><span className="font-semibold text-gray-700">Team:</span> {sanitizeInput(proj.teamType)}</p>}
                          {proj.description && <p className="mt-2 text-gray-700 bg-white p-2 rounded border border-gray-100 leading-relaxed">{sanitizeInput(proj.description)}</p>}
                        </div>
                        {(proj.projectUrl || proj.link) && (
                          <div className="pt-2 border-t border-gray-200 mt-2">
                            <a href={proj.projectUrl || proj.link} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs font-bold text-[#1a3884] hover:underline gap-1">
                              <Globe className="w-3.5 h-3.5" /> Project Link / URL
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No projects provided</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6">
              {/* Career & Personal Development Goals */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Career Goals */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#1a3884]" />
                    Career Goals
                  </h3>
                  {user.registrationDetails?.careerGoals ? (
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.08em]">Short Term</p>
                        <p className="text-gray-700 mt-1 leading-relaxed">{sanitizeInput(user.registrationDetails.careerGoals.shortTerm) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.08em]">Medium Term</p>
                        <p className="text-gray-700 mt-1 leading-relaxed">{sanitizeInput(user.registrationDetails.careerGoals.mediumTerm) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.08em]">Long Term</p>
                        <p className="text-gray-700 mt-1 leading-relaxed">{sanitizeInput(user.registrationDetails.careerGoals.longTerm) || 'N/A'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No career goals specified</p>
                  )}
                </div>

                {/* Personal Development Goals */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-[#1a3884]" />
                    Personal Development Goals
                  </h3>
                  {user.registrationDetails?.personalDevelopmentGoals ? (
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.08em]">Short Term</p>
                        <p className="text-gray-700 mt-1 leading-relaxed">{sanitizeInput(user.registrationDetails.personalDevelopmentGoals.shortTerm) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.08em]">Medium Term</p>
                        <p className="text-gray-700 mt-1 leading-relaxed">{sanitizeInput(user.registrationDetails.personalDevelopmentGoals.mediumTerm) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.08em]">Long Term</p>
                        <p className="text-gray-700 mt-1 leading-relaxed">{sanitizeInput(user.registrationDetails.personalDevelopmentGoals.longTerm) || 'N/A'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No development goals specified</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'extracurricular' && (
            <div className="space-y-6">
              {/* Extra-Curricular Activities */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#1a3884]" />
                  Extra-Curricular Activities
                </h3>
                {user.registrationDetails?.extracurricular && user.registrationDetails.extracurricular.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {user.registrationDetails.extracurricular.map((act, idx) => (
                      <div key={idx} className="border border-gray-100 rounded-lg p-4 bg-gray-50 space-y-2">
                        <div className="flex justify-between items-start">
                          <h4 className="font-bold text-gray-900 text-sm">{sanitizeInput(act.activityType)}</h4>
                          <span className="text-[10px] uppercase font-bold text-[#1a3884] bg-[#1a3884]/10 px-2 py-0.5 rounded-full">{sanitizeInput(act.level)}</span>
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed">{sanitizeInput(act.description)}</p>
                        {act.achievements && (
                          <div className="bg-white p-2 rounded border border-gray-100 text-xs mt-1">
                            <span className="font-bold text-gray-800 block mb-0.5">Achievements:</span>
                            <span className="text-gray-600">{sanitizeInput(act.achievements)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No extra-curricular records provided</p>
                )}
              </div>

              {/* Technical Certificates */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#1a3884]" />
                  Technical Certifications
                </h3>
                {user.registrationDetails?.certificates && user.registrationDetails.certificates.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {user.registrationDetails.certificates.map((cert, idx) => (
                      <div key={idx} className="border border-gray-100 rounded-lg p-4 bg-gray-50 space-y-2">
                        <div className="flex justify-between items-start flex-wrap gap-2">
                          <div>
                            <h4 className="font-bold text-gray-900 text-sm">{sanitizeInput(cert.title)}</h4>
                            <p className="text-xs font-semibold text-[#1a3884]">{sanitizeInput(cert.issuingOrg || cert.issuer)}</p>
                          </div>
                          {cert.yearOfCompletion && (
                            <span className="text-xs font-semibold text-gray-500 bg-white border border-gray-150 px-2 py-0.5 rounded">{sanitizeInput(cert.yearOfCompletion)}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          {cert.issueDate && <p><span className="font-semibold text-gray-700">Issue Date:</span> {new Date(cert.issueDate).toLocaleDateString()}</p>}
                          {cert.expiryDate && <p><span className="font-semibold text-gray-700">Expiry Date:</span> {new Date(cert.expiryDate).toLocaleDateString()}</p>}
                        </div>
                        <div className="pt-2 border-t border-gray-200 mt-2 flex flex-wrap gap-3">
                          {cert.certificateFile && (
                            <a href={getFileUrl(cert.certificateFile)} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs font-bold text-[#1a3884] hover:underline gap-1">
                              <FileText className="w-3.5 h-3.5" /> View Certificate
                            </a>
                          )}
                          {(cert.verificationUrl || cert.link) && (
                            <a href={cert.verificationUrl || cert.link} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs font-bold text-[#1a3884] hover:underline gap-1">
                              <Globe className="w-3.5 h-3.5" /> Verify Link
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No certification records provided</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'enrollments' && enrollments.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
              <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100 flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-[#1a3884]" />
                Course Enrollments
              </h3>
              <div className="space-y-3">
                {enrollments.map((enrollment) => (
                  <div
                    key={enrollment._id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">
                          {enrollment.course?.title || 'N/A'}
                        </h4>
                        <p className="text-sm text-gray-500">
                          Enrolled: {enrollment.enrollmentDate ? (() => {
                            const date = new Date(enrollment.enrollmentDate);
                            return !isNaN(date) ? date.toLocaleDateString() : 'N/A';
                          })() : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:space-x-4 self-start sm:self-auto w-full sm:w-auto">
                      <div className="w-full sm:w-32">
                        <div className="flex justify-between text-sm font-semibold text-gray-900 mb-1">
                          <span>Progress</span>
                          <span>{enrollment.overallProgress}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-full bg-gradient-to-r from-[#00152E] to-[#1a3884] rounded-full"
                            style={{ width: `${enrollment.overallProgress}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="self-start sm:self-auto shrink-0">
                        {getStatusBadge(enrollment.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Original Standard Non-Student Details View */
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100">Contact Information</h3>

              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#00152E]/10 rounded-lg">
                  <Mail className="w-5 h-5 text-[#1a3884]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.email) || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#00152E]/10 rounded-lg">
                  <Phone className="w-5 h-5 text-[#1a3884]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Mobile</p>
                  <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.mobile) || 'N/A'}</p>
                </div>
              </div>

              {user.address && (
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[#00152E]/10 rounded-lg">
                    <MapPin className="w-5 h-5 text-[#1a3884]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.address)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Professional Information */}
            <div className="space-y-4">
              <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100">Professional Information</h3>

              {user.college && (
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[#00152E]/10 rounded-lg">
                    <Building2 className="w-5 h-5 text-[#1a3884]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Institute</p>
                    <p className="text-sm font-medium text-gray-900">
                      {sanitizeInput(user.college.collegeName) || 'N/A'}
                    </p>
                  </div>
                </div>
              )}

              {user.qualification && (
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[#00152E]/10 rounded-lg">
                    <GraduationCap className="w-5 h-5 text-[#1a3884]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Qualification</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.qualification)}</p>
                  </div>
                </div>
              )}

              {user.specialization && (
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-[#00152E]/10 rounded-lg">
                    <Award className="w-5 h-5 text-[#1a3884]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Specialization</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.specialization)}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#00152E]/10 rounded-lg">
                  <Calendar className="w-5 h-5 text-[#1a3884]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Joined</p>
                  <p className="text-sm font-medium text-gray-900">
                    {user.createdAt ? (() => {
                      const date = new Date(user.createdAt);
                      return !isNaN(date) ? date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'N/A';
                    })() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          {(user.department || user.experience || user.bio) && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#0f225c] mb-4 pb-2.5 border-b border-gray-100">Additional Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {user.department && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Department</p>
                    <p className="text-sm font-medium text-gray-900">{sanitizeInput(user.department?.fullName || user.department?.abbreviation || user.department)}</p>
                  </div>
                )}
                {user.experience && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Experience</p>
                    <p className="text-sm font-medium text-gray-900">{user.experience} years</p>
                  </div>
                )}
                {user.bio && (
                  <div className="md:col-span-3">
                    <p className="text-xs text-gray-500 mb-1">Bio</p>
                    <p className="text-sm text-gray-900">{sanitizeInput(user.bio)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserDetails;
